import { STUDIO_STEPS, type StudioStep } from "@hali-shares/deck-schema";
import { isBlankDeck, minutesTotal, sectionStatus } from "../model";
import type { StudioCtx } from "./ctx";

const TITLES: Record<StudioStep, string> = {
  basics: "Basics",
  outline: "Outline",
  slides: "Slides",
  review: "Review",
};

function CheckIcon() {
  return (
    <svg
      aria-hidden="true"
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M2.5 6.2 L5 8.5 L9.5 3.5" />
    </svg>
  );
}

/** Where each step stands, in a few words, and whether it's done. */
export function stepState(ctx: StudioCtx, step: StudioStep): { note: string; done: boolean } {
  const { deck, settings } = ctx;
  const blank = isBlankDeck(deck);
  const n = deck.sections.length;
  switch (step) {
    case "basics": {
      const done = Boolean(settings.brief.goal?.trim()) && deck.title !== "Untitled talk";
      const minutes = settings.brief.minutes;
      return { done, note: done ? `${minutes ? `${minutes} min · ` : ""}goal set` : "Title, goal, length" };
    }
    case "outline":
      return blank
        ? { done: false, note: "Plan the sections" }
        : { done: true, note: `${n} section${n === 1 ? "" : "s"} · ${minutesTotal(deck)} min` };
    case "slides": {
      if (blank) return { done: false, note: "Section by section" };
      const status = deck.sections.map((s) => sectionStatus(s, settings.plan[s.id]));
      const reviewed = status.filter((s) => s === "reviewed").length;
      const empty = status.filter((s) => s === "empty").length;
      return {
        done: reviewed === n,
        note: empty ? `${n - empty} of ${n} drafted` : `${reviewed} of ${n} reviewed`,
      };
    }
    case "review": {
      const fix = ctx.findings.filter((f) => f.level === "fix").length;
      return { done: !blank && fix === 0, note: blank ? "Check, rehearse, go live" : fix ? `${fix} to fix` : "Ready" };
    }
  }
}

/** The four steps of building a talk. Any step can be opened at any time; the current one is remembered. */
export function Stepper({ ctx, current }: { ctx: StudioCtx; current: StudioStep }) {
  return (
    <nav className="stepper" aria-label="Steps">
      <ol>
        {STUDIO_STEPS.map((step, i) => {
          const { note, done } = stepState(ctx, step);
          return (
            <li key={step}>
              <button
                type="button"
                className="step-tab"
                aria-current={step === current ? "step" : undefined}
                data-done={done}
                onClick={() => ctx.goto(step)}
              >
                <span className="step-num">{done ? <CheckIcon /> : i + 1}</span>
                <span className="step-text">
                  <span className="step-title">{TITLES[step]}</span>
                  <span className="step-note">{note}</span>
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
